<script setup lang="ts">
import '@toolify/client/src/modules/notifications/styles/messages/index.scss'
import {computed, nextTick, onBeforeUnmount, onBeforeUpdate, onMounted, onUpdated, ref, shallowRef, toRaw, watch} from 'vue'
import {
  useDebounceFn,
  useDocumentVisibility,
  useEventListener,
  usePreferredReducedMotion,
  useResizeObserver,
} from '@vueuse/core'
import LocalMessageCardComponent from '@toolify/client/src/modules/notifications/components/LocalMessageCardComponent.vue'
import LocalMessageOverflowPillComponent from '@toolify/client/src/modules/notifications/components/LocalMessageOverflowPillComponent.vue'
import {ILocalMessage} from '@toolify/client/src/modules/notifications/types/ILocalMessage'
import {MessageStateType} from '@toolify/client/src/modules/notifications/enum/MessageStateType'
import {
  computeFloatingStackLayout,
  FLOATING_STACK_MAX_VISIBLE,
  IFloatingStackLayoutEntry,
} from '@toolify/client/src/modules/notifications/functions/computeFloatingStackLayout'
import {getLocalMessagePinRank} from '@toolify/client/src/modules/notifications/functions/getLocalMessagePinRank'
import {useLocalMessageStore} from '@toolify/client/src/stores/LocalMessageStore/useLocalMessageStore'
import {LocalMessagePauseReasonType} from '@toolify/client/src/stores/LocalMessageStore/enum/LocalMessagePauseReasonType'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'

// The bottom-right stack of local message cards with the "+N" pill, and the renderer the local message
// store waits for. A host may put its own entries (server popups) into the same stack: it lays them out
// through getExternalEntries (keys must not start with "local:"), renders them in the #externalEntry slot
// and learns from the externalLayout event which of them are folded into the pill.
interface Props {
  getExternalEntries?: () => IFloatingStackLayoutEntry[]
  // changes whenever the external entries do, so the stack lays itself out again
  externalRevision?: string
}

const props = withDefaults(defineProps<Props>(), {
  getExternalEntries: () => [],
  externalRevision: '',
})

interface Emits {
  (e: 'externalLayout', collapsedKeys: string[]): void
  // the pill's "Close all" closed the local flow cards; the host closes its own entries
  (e: 'closeAll'): void
}

const emit = defineEmits<Emits>()

type IRenderedStackEntry =
  | {kind: 'external', key: string, renderKey: string}
  | {kind: 'local', key: string, renderKey: string, message: ILocalMessage}

interface IStackLayoutState {
  orderedKeys: string[]
  collapsedKeys: Set<string>
  overflowCount: number
  overflowSeverities: string[]
  visibleCount: number
  pinnedCount: number
  maxVisible: number
}

const LOCAL_KEY_PREFIX = 'local:'
const RESIZE_DEBOUNCE_MS = 120
const EXTERNAL_LEAVE_FALLBACK_MS = 300
const GLIDE_MS = 220

const localMessageStore = useLocalMessageStore()
const preferredReducedMotion = usePreferredReducedMotion()
const documentVisibility = useDocumentVisibility()
const {getThemeVariable} = useThemeVariables()
const preferBorders = computed(() => getThemeVariable.value(ThemeVariableType.PreferBorders))

const rootRef = ref<HTMLElement | null>(null)
const stackRef = ref<HTMLElement | null>(null)
const cssMaxVisible = ref<number | null>(null)
const isStackOverflowing = ref(false)
const stackLayout = shallowRef<IStackLayoutState>({
  orderedKeys: [],
  collapsedKeys: new Set(),
  overflowCount: 0,
  overflowSeverities: [],
  visibleCount: 0,
  pinnedCount: 0,
  maxVisible: FLOATING_STACK_MAX_VISIBLE,
})

// a push that reuses the id of a leaving card is a new card: it must mount again to run its enter step
const localRenderKeys = new WeakMap<ILocalMessage, string>()
let localRenderSequence = 0
let hasRemovedEntries = false
// distance of every stack child from the stack's bottom edge before a removal reached the DOM
let bottomOffsetsBeforeRemoval: Map<Element, number> | null = null
let focusedBeforeUpdate: HTMLElement | null = null
let focusedBeforeStack: HTMLElement | null = null
let renderedEntryCount = 0

const isReducedMotion = computed(() => preferredReducedMotion.value === 'reduce')

const renderedEntries = computed<IRenderedStackEntry[]>(() => {
  const localMessagesById = new Map(localMessageStore.getMessages().map(message => [message.id, message]))
  return stackLayout.value.orderedKeys.flatMap<IRenderedStackEntry>(key => {
    if(!key.startsWith(LOCAL_KEY_PREFIX)) {
      return [{
        kind: 'external',
        key,
        renderKey: key,
      }]
    }
    const message = localMessagesById.get(key.slice(LOCAL_KEY_PREFIX.length))
    return message ? [{
      kind: 'local',
      key,
      renderKey: getLocalRenderKey(message),
      message,
    }] : []
  })
})

function getLocalRenderKey(message: ILocalMessage): string {
  const rawMessage = toRaw(message)
  let renderKey = localRenderKeys.get(rawMessage)
  if(!renderKey) {
    renderKey = `${LOCAL_KEY_PREFIX}${message.id}#${++localRenderSequence}`
    localRenderKeys.set(rawMessage, renderKey)
  }
  return renderKey
}

function buildLocalLayoutEntries(): IFloatingStackLayoutEntry[] {
  return localMessageStore.getMessages().map<IFloatingStackLayoutEntry>(message => {
    const interaction = localMessageStore.getInteraction(message.id)
    return {
      key: `${LOCAL_KEY_PREFIX}${message.id}`,
      queuedAt: message.createdAt,
      seq: message.seqNo,
      isPinned: message.pinned,
      pinRank: getLocalMessagePinRank(message),
      severity: message.severity,
      isHovered: interaction.hovered,
      isFocused: interaction.focused,
      isTouched: interaction.touched,
      isLeaving: message.state === MessageStateType.Leave,
      isCollapsed: message.collapsed,
    }
  })
}

// Runs at the same moments as the prototype's layout(): push, ×N, update, removal, resize
function relayout(showAllOnce = false) {
  const entries = [...props.getExternalEntries(), ...buildLocalLayoutEntries()]
  const layout = computeFloatingStackLayout(entries, {
    cssMaxVisible: cssMaxVisible.value,
    showAllOnce,
  })
  const orderedKeys = layout.ordered.map(entry => entry.key)
  const nextKeys = new Set(orderedKeys)
  if(stackLayout.value.orderedKeys.some(key => !nextKeys.has(key))) {
    hasRemovedEntries = true
  }
  stackLayout.value = {
    orderedKeys,
    collapsedKeys: new Set(layout.collapsedKeys),
    overflowCount: layout.overflowCount,
    overflowSeverities: layout.overflowSeverities,
    visibleCount: layout.visibleCount,
    pinnedCount: layout.pinnedCount,
    maxVisible: layout.maxVisible,
  }

  const localCollapsedIds: string[] = []
  const externalCollapsedKeys: string[] = []
  layout.collapsedKeys.forEach(key => {
    if(key.startsWith(LOCAL_KEY_PREFIX)) {
      localCollapsedIds.push(key.slice(LOCAL_KEY_PREFIX.length))
    } else {
      externalCollapsedKeys.push(key)
    }
  })
  localMessageStore.applyLayout(localCollapsedIds, layout.overflowCount)
  emit('externalLayout', externalCollapsedKeys)
}

// the card look may cap the visible cards (--msg-max-visible: the mobile bottom sheet shows one)
function readStyleLimits() {
  const root = rootRef.value
  if(!root) {
    return
  }
  const computedStyle = getComputedStyle(root)
  const maxVisible = parseInt(computedStyle.getPropertyValue('--msg-max-visible'), 10)
  cssMaxVisible.value = Number.isFinite(maxVisible) && maxVisible > 0 ? maxVisible : null
}

const onWindowResize = useDebounceFn(() => {
  readStyleLimits()
  relayout()
  void nextTick(syncStackOverflow)
}, RESIZE_DEBOUNCE_MS)

function isCollapsedEntry(key: string): boolean {
  return stackLayout.value.collapsedKeys.has(key)
}

function focusNewestLocalCard(): boolean {
  const id = localMessageStore.getNewestVisibleMessageId()
  const card = id ? stackRef.value?.querySelector<HTMLElement>(`.msg[data-msg-id="${CSS.escape(id)}"]`) : null
  if(!card) {
    return false
  }
  const target = card.querySelector<HTMLElement>('button, a') ?? card
  target.focus({preventScroll: true})
  return true
}

function hasKeyboardFocusInPill(): boolean {
  const activeElement = document.activeElement
  const pill = stackRef.value?.querySelector('.msg-overflow')
  if(!pill || !(activeElement instanceof HTMLElement) || !pill.contains(activeElement)) {
    return false
  }
  try {
    return activeElement.matches(':focus-visible')
  } catch {
    return true
  }
}

// "Show" / "Close all" hide the pill under the focused button: a keyboard user continues on the newest
// card, or where the focus came from before it entered the stack
function moveFocusOffPill() {
  if(focusNewestLocalCard()) {
    return
  }
  if(focusedBeforeStack?.isConnected) {
    focusedBeforeStack.focus({preventScroll: true})
    return
  }
  const activeElement = document.activeElement
  if(activeElement instanceof HTMLElement && rootRef.value?.contains(activeElement)) {
    activeElement.blur()
  }
}

function showAllMessages() {
  const shouldMoveFocus = hasKeyboardFocusInPill()
  relayout(true)
  if(shouldMoveFocus) {
    void nextTick(moveFocusOffPill)
  }
}

function closeAllMessages() {
  const shouldMoveFocus = hasKeyboardFocusInPill()
  localMessageStore.dismissAllInFlow()
  emit('closeAll')
  if(shouldMoveFocus) {
    void nextTick(moveFocusOffPill)
  }
}

function onRootFocusIn(event: FocusEvent) {
  const previousElement = event.relatedTarget
  if(previousElement instanceof HTMLElement && !rootRef.value?.contains(previousElement)) {
    focusedBeforeStack = previousElement
  }
}

// External entries leave through their own CSS transition (TransitionGroup classes); a local card is
// already invisible (data-state="leave") when it leaves the list, so it goes at once
function onEntryLeave(element: Element, done: () => void) {
  if(!(element instanceof HTMLElement) || element.dataset.kind === 'local') {
    done()
    return
  }
  let isFinished = false
  const finish = () => {
    if(isFinished) {
      return
    }
    isFinished = true
    element.removeEventListener('transitionend', onTransitionEnd)
    clearTimeout(fallbackTimeout)
    done()
  }
  const onTransitionEnd = (event: TransitionEvent) => {
    if(event.target === element) {
      finish()
    }
  }
  element.addEventListener('transitionend', onTransitionEnd)
  const fallbackTimeout = setTimeout(finish, EXTERNAL_LEAVE_FALLBACK_MS)
}

function measureBottomOffsets(): Map<Element, number> {
  const offsets = new Map<Element, number>()
  const stack = stackRef.value
  if(!stack) {
    return offsets
  }
  const stackBottom = stack.getBoundingClientRect().bottom
  Array.from(stack.children).forEach(child => {
    offsets.set(child, stackBottom - child.getBoundingClientRect().bottom)
  })
  return offsets
}

// The stack is anchored at the bottom: when an entry goes, only what was above it drops. Those entries
// start where they were and glide down; the ones below it (often the hovered one) do not move.
function glideAfterRemoval() {
  const stack = stackRef.value
  const offsetsBefore = bottomOffsetsBeforeRemoval
  if(!stack || !offsetsBefore) {
    return
  }
  const isRemovalInDom = Array.from(offsetsBefore.keys()).some(child => child.parentElement !== stack)
  const offsetsNow = measureBottomOffsets()
  if(!isRemovalInDom) {
    // an external entry still runs its leave transition: wait for its removal with fresh positions
    bottomOffsetsBeforeRemoval = offsetsNow
    return
  }
  bottomOffsetsBeforeRemoval = null
  if(isReducedMotion.value) {
    return
  }
  offsetsNow.forEach((offsetNow, child) => {
    const drop = (offsetsBefore.get(child) ?? offsetNow) - offsetNow
    if(drop > 0.5 && typeof child.animate === 'function') {
      // `translate` stays clear of the cards' own transform transitions
      child.animate([{translate: `0 ${-drop}px`}, {translate: '0 0'}], {
        duration: GLIDE_MS,
        easing: 'ease',
      })
    }
  })
}

// Pinned cards never fold into the pill, so the capped stack may not hold them all: it then scrolls,
// and is brought to its end (pinned and newest cards) when it starts to overflow or gets a new card
function syncStackOverflow() {
  const stack = stackRef.value
  if(!stack) {
    return
  }
  const entryCount = renderedEntries.value.length
  const hasNewEntry = entryCount > renderedEntryCount
  renderedEntryCount = entryCount
  const wasOverflowing = isStackOverflowing.value
  const isOverflowing = stack.scrollHeight > stack.clientHeight + 1
  isStackOverflowing.value = isOverflowing
  if(isOverflowing && (!wasOverflowing || hasNewEntry)) {
    void nextTick(() => {
      stack.scrollTop = stack.scrollHeight
    })
  }
}

useResizeObserver(stackRef, () => {
  glideAfterRemoval()
  syncStackOverflow()
})

// Tab / F8 = keyboard modality (cards with an action then live at least 10 s); F8 jumps to the newest card
useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if(event.key === 'Tab' || event.key === 'F8') {
    localMessageStore.setKeyboardModality(true)
  }
  if(event.key === 'F8' && focusNewestLocalCard()) {
    event.preventDefault()
  }
})

useEventListener(document, 'pointerdown', () => {
  localMessageStore.setKeyboardModality(false)
}, {capture: true})

useEventListener(window, 'resize', onWindowResize)

watch([() => localMessageStore.layoutRevision, () => props.externalRevision], () => {
  relayout()
})

watch(documentVisibility, visibility => {
  localMessageStore.setPausedByEnvironment(LocalMessagePauseReasonType.DocumentHidden, visibility !== 'visible')
}, {immediate: true})

onBeforeUpdate(() => {
  // Vue moves nodes with insertBefore when the order changes; a moved node loses the focus
  const activeElement = document.activeElement
  focusedBeforeUpdate = activeElement instanceof HTMLElement && stackRef.value?.contains(activeElement)
    ? activeElement
    : null
  if(hasRemovedEntries) {
    hasRemovedEntries = false
    bottomOffsetsBeforeRemoval = measureBottomOffsets()
  }
})

onUpdated(() => {
  const element = focusedBeforeUpdate
  focusedBeforeUpdate = null
  if(element?.isConnected && document.activeElement === document.body) {
    element.focus({preventScroll: true})
  }
  glideAfterRemoval()
  syncStackOverflow()
})

relayout()

onMounted(() => {
  readStyleLimits()
  relayout()
  localMessageStore.setPausedByEnvironment(LocalMessagePauseReasonType.RendererDetached, false)
})

onBeforeUnmount(() => {
  localMessageStore.setPausedByEnvironment(LocalMessagePauseReasonType.RendererDetached, true)
})
</script>

<template>
  <div
    id="messages-root"
    ref="rootRef"
    class="messages-root"
    :class="{'prefer-borders': preferBorders}"
    data-density="comfortable"
    :data-reduced-motion="isReducedMotion ? '1' : '0'"
    @focusin="onRootFocusIn"
  >
    <!-- the host's own surfaces next to the stack (the mobile popup pill) -->
    <slot/>
    <div
      ref="stackRef"
      class="msg-stack"
      data-position="bottom-right"
      :data-overflow="stackLayout.overflowCount"
      :data-visible="stackLayout.visibleCount"
      :data-pinned="stackLayout.pinnedCount"
      :data-max-visible="stackLayout.maxVisible"
      :data-overflowing="isStackOverflowing ? '1' : '0'"
    >
      <LocalMessageOverflowPillComponent
        :count="stackLayout.overflowCount"
        :severities="stackLayout.overflowSeverities"
        @showAll="showAllMessages"
        @closeAll="closeAllMessages"
      />
      <!-- no FLIP moves: the prototype never animates a reorder, and .msg transitions transform -->
      <TransitionGroup
        name="floating-notification"
        moveClass="floating-notification-no-move"
        @leave="onEntryLeave"
      >
        <template v-for="entry in renderedEntries" :key="entry.renderKey">
          <slot
            v-if="entry.kind === 'external'"
            name="externalEntry"
            :entryKey="entry.key"
            :isCollapsed="isCollapsedEntry(entry.key)"
          />
          <LocalMessageCardComponent
            v-else
            :message="entry.message"
          />
        </template>
      </TransitionGroup>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .msg-stack {
    pointer-events: none;
  }

  // TransitionGroup skips its FLIP pass when the move class has no transform transition
  .floating-notification-no-move {
    transition: none !important;
  }
</style>
